/**
 * Pure wallboard logic for the Roost dashboard (WI 34272): seat context,
 * unanswered user inbounds, queue depth and display helpers. Account pace
 * lives in pace.logic.ts, machines in machines.logic.ts. No Angular, no I/O.
 */
import type { ContextSnapshot } from '../models/flock-telemetry.models';
import type { SeatWorkQueue } from '../models/work-queue.model';
import { parseUtcMs } from '../models/flock-telemetry.utils';

// ── Thresholds (Tim, WI 34272) ──────────────────────────────────────────────

/** "Seats with >60% context." Strictly greater than, per the ask. */
export const CONTEXT_ALERT_PCT = 60;
/** "Seats that are not responding within 2 minutes to a user." */
export const UNANSWERED_AFTER_MS = 2 * 60_000;

// ── Seat context ────────────────────────────────────────────────────────────

export interface ContextAlert {
  instanceId: string;
  percent: number;
  host: string | null;
  cycleStatus: ContextSnapshot['cycleStatus'];
  ageMinutes: number;
}

/** Seats strictly over 60% context, hottest first. Reaped seats are gone. */
export function buildContextAlerts(
  snapshots: ContextSnapshot[],
  nowMs: number
): ContextAlert[] {
  return snapshots
    .filter(
      (s) => s.percentUsed > CONTEXT_ALERT_PCT && s.processState !== 'reaped'
    )
    .map((s) => {
      const observed = parseUtcMs(s.observedAt);
      return {
        instanceId: s.instanceId,
        percent: s.percentUsed,
        host: s.reportingHost,
        cycleStatus: s.cycleStatus,
        ageMinutes:
          observed === null
            ? 0
            : Math.max(0, Math.round((nowMs - observed) / 60_000)),
      };
    })
    .sort(
      (a, b) =>
        b.percent - a.percent || a.instanceId.localeCompare(b.instanceId)
    );
}

// ── Unanswered user inbounds ────────────────────────────────────────────────

/** Per-row inbound detail the work-queue endpoint ships (WI 31657). The shared
 * SeatWorkQueue model predates it, so it is typed locally as optional. */
export interface WireOpenInbound {
  chatId: string;
  channel: string | null;
  senderName: string | null;
  receivedAt: string | null;
  slaDueAt: string | null;
  isOverdue: boolean;
  state: string | null;
  parkedReason: string | null;
}

export type WallboardSeat = SeatWorkQueue & {
  openInbounds?: WireOpenInbound[];
};

export interface UnansweredInbound {
  instanceId: string;
  senderName: string;
  channel: string;
  /** The inbound's chat, so the row can open it (WI 34329). */
  chatId: string;
  /** Set when a seat parked the thread; such a row is listed, never a live wait. */
  parkedReason: string | null;
  waitingMinutes: number;
  /** When the inbound arrived, so a Dismiss hides only this inbound, not a
   * later one on the same chat (Bug 35644). */
  receivedAtMs: number;
}

/**
 * User inbounds still needing a response more than 2 minutes after they
 * arrived, longest live wait first. Parked rows (a seat set them aside with a
 * reason) are not "unresponsive": they follow every live row, carrying the
 * reason so a stale one is visibly stale (WI 34329).
 */
export function buildUnansweredInbounds(
  seats: WallboardSeat[],
  nowMs: number
): UnansweredInbound[] {
  const out: UnansweredInbound[] = [];
  for (const seat of seats) {
    if (seat.retired) continue;
    for (const oi of seat.openInbounds ?? []) {
      const received = parseUtcMs(oi.receivedAt);
      if (received === null) continue;
      const waited = nowMs - received;
      if (waited <= UNANSWERED_AFTER_MS) continue;
      out.push({
        instanceId: seat.instanceId,
        senderName: oi.senderName ?? 'unknown sender',
        channel: oi.channel ?? 'unknown',
        chatId: oi.chatId,
        parkedReason:
          oi.parkedReason || oi.state === 'parked'
            ? oi.parkedReason || 'parked'
            : null,
        waitingMinutes: Math.floor(waited / 60_000),
        receivedAtMs: received,
      });
    }
  }
  return out.sort(
    (a, b) =>
      Number(a.parkedReason !== null) - Number(b.parkedReason !== null) ||
      b.waitingMinutes - a.waitingMinutes ||
      a.instanceId.localeCompare(b.instanceId)
  );
}

/**
 * Deep link that opens a Teams chat, in the shape Graph's chat webUrl uses
 * (encoded chatId, then /0). Null for anything that is not a Teams chat id,
 * so a non-Teams row renders no dead link (WI 34329).
 */
export function teamsChatDeepLink(
  channel: string | null,
  chatId: string | null
): string | null {
  if (channel?.toLowerCase() !== 'teams' || !chatId?.startsWith('19:')) {
    return null;
  }
  return `https://teams.microsoft.com/l/chat/${encodeURIComponent(chatId)}/0`;
}

/** One row of GET /api/ryn-db/open-inbound-depths (PascalCase on the wire). */
export interface QueueDepthRow {
  InstanceId: string;
  OpenCount: number;
  OverdueCount: number;
  ParkedCount: number;
  OldestSlaDueAt: string | null;
}

export interface QueueDepthSummary {
  open: number;
  /** Unparked rows past the bridge's own SLA (5 min today, not Tim's 2 min). */
  overduePastSla: number;
  parked: number;
  seatsWithOpen: number;
  /** Seat with the most unparked open rows; null when none are unparked. */
  busiestSeat: string | null;
  busiestActive: number;
}

/** Flock-wide queue depth line for the unanswered tile (WI 34280 summary). */
export function summarizeQueueDepths(rows: QueueDepthRow[]): QueueDepthSummary {
  let busiestSeat: string | null = null;
  let busiestActive = 0;
  const summary = rows.reduce(
    (acc, r) => {
      const active = r.OpenCount - r.ParkedCount;
      if (active > busiestActive) {
        busiestActive = active;
        busiestSeat = r.InstanceId;
      }
      return {
        open: acc.open + r.OpenCount,
        overduePastSla: acc.overduePastSla + r.OverdueCount,
        parked: acc.parked + r.ParkedCount,
        seatsWithOpen: acc.seatsWithOpen + (r.OpenCount > 0 ? 1 : 0),
      };
    },
    { open: 0, overduePastSla: 0, parked: 0, seatsWithOpen: 0 }
  );
  return { ...summary, busiestSeat, busiestActive };
}

// ── Display helpers ─────────────────────────────────────────────────────────

/** "4m", "2h 05m", "3d 4h". Negative or null reads as an em-free dash. */
export function formatDuration(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms) || ms < 0) return '-';
  const totalMin = Math.floor(ms / 60_000);
  if (totalMin < 60) return `${totalMin}m`;
  const hours = Math.floor(totalMin / 60);
  if (hours < 24) return `${hours}h ${String(totalMin % 60).padStart(2, '0')}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

export function formatGb(bytes: number | null): string {
  if (bytes === null || bytes <= 0) return '';
  return `${Math.round(bytes / 1024 ** 3)} GB`;
}

/** Split a list into what fits on the no-scroll tile and the overflow count. */
export function fitList<T>(
  items: T[],
  max: number
): { visible: T[]; more: number } {
  return {
    visible: items.slice(0, max),
    more: Math.max(0, items.length - max),
  };
}
